/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useWallets } from "../../../../hooks/useWallets";
import { TransactionType, Wallet } from "../components/types";
import { hasStepFlow } from "../helpers";
import { TransactionFormState } from "./useTransactionFormState";

type Step = {
  condition: () => boolean;
  action: () => void;
};

export const useTransactionFormEffects = (formState: TransactionFormState) => {
  const { t } = useTranslation(['transactions']);
  const { getWalletById, getWalletByName } = useWallets();

  const {
    location,
    transaction,
    isUpdate,
    currentTab,
    firstRender,
    setFirstRender,
    transactionTypeConfirmed,
    setTransactionTypeConfirmed,
    transactionTypeConfirmedRef,
    loanType,
    setLoanType,
    creditCardType,
    amount,
    setAmount,
    description,
    setDescription,
    datePicker,
    setDatePicker,
    spendingCategory,
    setSpendingCategory,
    incomeCategory,
    setIncomeCategory,
    expenseWallet,
    setExpenseWallet,
    incomeWallet,
    setIncomeWallet,
    transferSourceWallet,
    setTransferSourceWallet,
    selectDestinationWallet,
    setSelectDestinationWallet,
    repaymentDate,
    numberOfInstallments,
    setAmountError,
    setCurrentTransactionType,
    setInitialLoanWallet,
    setInitialDebtWallet,
    openTransactionTypeSelector,
    openDatePicker,
    openDialogSelectWallets,
    openDialogSelectDestinationWallets,
    openDialogSelectedExpenseCategory,
    openDialogSelectedIncomeCategory,
    setOpenTransactionTypeSelector,
    setOpenDialogSelectedExpenseCategory,
    setOpenDialogSelectedIncomeCategory,
    setOpenDialogSelectWallets,
    setOpenDialogSelectDestinationWallets,
    setOpenDatePicker,
    handleOnCloseDialog,
    amountInputRef,
    descriptionInputRef,
    installmentTermInputRef,
    isResettingWalletsRef,
    walletResetCounter,
    isManuallyOpeningTypeSelectorRef,
    // loanWallet,
    setLoanWallet,
    // repaymentWallet,
    setRepaymentWallet,
  } = formState;

  // Build step configuration
  const buildStepsConfig = (): Record<TransactionType, Step[]> => {
    const transactionTypeStep: Step = {
      condition: () => transactionTypeConfirmedRef.current,
      action: () => setOpenTransactionTypeSelector(true)
    };
    const datePickerStep: Step = { condition: () => !!datePicker, action: () => setOpenDatePicker(true) };
    const descriptionStep: Step = {
      condition: () => !!description,
      action: () => { descriptionInputRef.current?.focus(); descriptionInputRef.current?.click(); }
    };

    return {
      [TransactionType.EXPENSE]: [
        transactionTypeStep,
        { condition: () => !!spendingCategory, action: () => setOpenDialogSelectedExpenseCategory(true) },
        { condition: () => !!expenseWallet, action: () => setOpenDialogSelectWallets(true) },
        datePickerStep,
        descriptionStep
      ],
      [TransactionType.INCOME]: [
        transactionTypeStep,
        { condition: () => !!incomeCategory, action: () => setOpenDialogSelectedIncomeCategory(true) },
        { condition: () => !!incomeWallet, action: () => setOpenDialogSelectWallets(true) },
        datePickerStep,
        descriptionStep
      ],
      [TransactionType.TRANSFER]: [
        transactionTypeStep,
        { condition: () => !!transferSourceWallet, action: () => setOpenDialogSelectWallets(true) },
        { condition: () => !!selectDestinationWallet, action: () => setOpenDialogSelectDestinationWallets(true) },
        datePickerStep,
        descriptionStep
      ],
      [TransactionType.LOAN]: [],
      [TransactionType.CREDIT_CARD]: creditCardType === 'convert_installment' ? [
        transactionTypeStep,
        { condition: () => !!selectDestinationWallet, action: () => setOpenDialogSelectDestinationWallets(true) },
        { condition: () => !!numberOfInstallments, action: () => { setTimeout(() => { installmentTermInputRef.current?.focus(); installmentTermInputRef.current?.click(); }, 300); } },
        datePickerStep,
        descriptionStep
      ] : [
        transactionTypeStep,
        { condition: () => !!transferSourceWallet, action: () => setOpenDialogSelectWallets(true) },
        { condition: () => !!selectDestinationWallet, action: () => setOpenDialogSelectDestinationWallets(true) },
        datePickerStep,
        descriptionStep
      ]
    };
  };

  const selectorCallback = () => {
    // Skip if user is manually opening the Transaction Type selector
    // This flag is set on mousedown (before useClickOutside fires) and cleared after dialog opens
    if (isManuallyOpeningTypeSelectorRef.current) return;

    const isLoanWithoutSteps = currentTab === TransactionType.LOAN && loanType !== 'borrow-expense';
    if (firstRender || !amount || isLoanWithoutSteps || isUpdate) return;

    const configKey = (currentTab === TransactionType.LOAN && loanType === 'borrow-expense')
      ? TransactionType.EXPENSE
      : currentTab;
    const stepsConfig = buildStepsConfig();
    const config = stepsConfig[configKey];
    for (const step of config) {
      if (!step.condition()) {
        step.action();
        return;
      }
    }
  };

  const validateAmount = () => {
    if (amount === null) {
      setAmountError(null);
      return;
    }

    let selectedWallet: Wallet | null = null;
    if (currentTab === TransactionType.EXPENSE) {
      selectedWallet = expenseWallet;
    } else if (currentTab === TransactionType.INCOME) {
      selectedWallet = incomeWallet;
    } else if (currentTab === TransactionType.TRANSFER) {
      selectedWallet = transferSourceWallet;
    }

    if (!selectedWallet) {
      setAmountError(null);
      return;
    }

    if (selectedWallet.wallet.type === 'credit') {
      setAmountError(null);
    } else {
      if (amount < 0) {
        setAmountError(t('error.amount_must_be_positive', { ns: 'transactions' }));
      } else {
        setAmountError(null);
      }
    }
  };

  const handleAmountFocus = () => {
    if (firstRender || isUpdate) return;
    if (!transactionTypeConfirmedRef.current) {
      setOpenTransactionTypeSelector(true);
    }
  };

  // Initialize transaction data for edit mode
  useEffect(() => {
    if (transaction) {
      const transactionType = transaction?.type;
      const [categoryItem, category] = transaction?.category?.name?.split?.('•') || [];
      setAmount(Math.abs(transaction?.amount || 0) || null);
      setDescription(transaction?.description || '');

      transactionTypeConfirmedRef.current = true;
      setTransactionTypeConfirmed(true);

      if (transactionType === 'transfer') {
        setCurrentTransactionType('transfer');
        setSpendingCategory(null);
      } else if (transactionType === 'borrow-expense') {
        setCurrentTransactionType('loan');
        setLoanType('borrow-expense');
        if (!!categoryItem && !!category) {
          setSpendingCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
        }
      } else if (transactionType === 'borrow') {
        setCurrentTransactionType('loan');
        setLoanType('borrow');
        // borrow transactions might have a category or not
        if (!!categoryItem && !!category) {
          setIncomeCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
        }
      } else if (transactionType === 'repay') {
        setCurrentTransactionType('loan');
        setLoanType('repay');
        // repay transactions might have a category or not
        if (!!categoryItem && !!category) {
          setIncomeCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
        }
      } else if (transactionType === 'lend') {
        setCurrentTransactionType('loan');
        setLoanType('lend');
        // lend transactions might have a category or not
        if (!!categoryItem && !!category) {
          setIncomeCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
        }
      } else if (transactionType === 'collect') {
        setCurrentTransactionType('loan');
        setLoanType('collect');
        // collect transactions might have a category or not
        if (!!categoryItem && !!category) {
          setIncomeCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
        }
      } else if (transactionType === 'expense') {
        setCurrentTransactionType('expense');
        if (!!categoryItem && !!category) {
          setSpendingCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
        }
      } else if (transactionType === 'income') {
        setCurrentTransactionType('income');
        setSpendingCategory(null);
        setIncomeCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
      } else if (!!categoryItem && !!category) {
        setCurrentTransactionType('expense');
        setSpendingCategory({ id: transaction?.categoryId || '', name: categoryItem, category });
      }
      setDatePicker(dayjs(transaction?.transactionDate));
    }
  }, [transaction]);

  // Initialize wallet data for edit mode
  useEffect(() => {
    if (transaction) {
      const wallet = getWalletById(transaction?.walletId || '');
      const loanWallet = getWalletById((transaction as any)['loanWallet']?.id || '');
      if (wallet || loanWallet) {
        switch (transaction?.type) {
          case "expense":
          case "borrow-expense":
            setExpenseWallet({ wallet } as unknown as Wallet);
            break;
          case "income":
            setIncomeWallet({ wallet } as unknown as Wallet);
            break;
          case "collect":
            setLoanWallet({wallet: loanWallet} as unknown as Wallet);
            setRepaymentWallet({ wallet } as unknown as Wallet);
            break;
          case "lend":
            setLoanWallet({wallet: loanWallet} as unknown as Wallet);
            setRepaymentWallet({ wallet } as unknown as Wallet);
            break;
          case "repay":
            setLoanWallet({wallet: loanWallet} as unknown as Wallet);
            setRepaymentWallet({ wallet } as unknown as Wallet);
            break;
          case "borrow":
            setLoanWallet({wallet: loanWallet} as unknown as Wallet);
            setRepaymentWallet({ wallet } as unknown as Wallet);
            break;
          case "transfer":
            const sourceWalletName = transaction?.transfer?.sourceWallet;
            const destinationWalletName = transaction?.transfer?.destinationWallet;
            const srcWallet = getWalletByName(sourceWalletName || '');
            const destWallet = getWalletByName(destinationWalletName || '');
            if (srcWallet) setTransferSourceWallet({ wallet: srcWallet } as unknown as Wallet);
            if (destWallet) setSelectDestinationWallet({ wallet: destWallet } as unknown as Wallet);
            break;
        }
      }
    }
  }, [transaction]);

  // Run selector callback on relevant state changes
  useEffect(() => {
    // Skip if wallets are being reset (during transaction type change)
    if (isResettingWalletsRef.current) return;
    selectorCallback();
    // walletResetCounter triggers this effect to re-run after reset is complete
  }, [currentTab, loanType, creditCardType, transactionTypeConfirmed, incomeCategory, spendingCategory, expenseWallet, incomeWallet, transferSourceWallet, selectDestinationWallet, datePicker, repaymentDate, amount, walletResetCounter]);

  // Auto-focus description on mobile when form is complete
  useEffect(() => {
    const isMobile = window.innerWidth < 640;
    const isBorrowExpense = currentTab === TransactionType.LOAN && loanType === 'borrow-expense';
    let focusTimer: ReturnType<typeof setTimeout> | null = null;

    if (isMobile && amount &&
      ((currentTab === TransactionType.EXPENSE && spendingCategory && expenseWallet && datePicker) ||
        (isBorrowExpense && spendingCategory && expenseWallet && datePicker) ||
        (currentTab === TransactionType.INCOME && incomeCategory && incomeWallet && datePicker) ||
        (currentTab === TransactionType.LOAN && loanType !== 'borrow-expense' && incomeCategory && incomeWallet && datePicker && repaymentDate) ||
        (currentTab === TransactionType.TRANSFER && transferSourceWallet && selectDestinationWallet && datePicker))) {
      if (descriptionInputRef.current && !description) {
        focusTimer = setTimeout(() => {
          descriptionInputRef.current?.focus();
          descriptionInputRef.current?.click();
        }, 300);
      }
    }

    return () => {
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [amount, currentTab, loanType, spendingCategory, expenseWallet, incomeCategory, incomeWallet, transferSourceWallet, selectDestinationWallet, datePicker, description, repaymentDate]);

  // Validate amount on changes
  useEffect(() => {
    validateAmount();
  }, [amount, expenseWallet, incomeWallet, transferSourceWallet]);

  // Set first render to false
  useEffect(() => {
    setFirstRender(false);
  }, []);

  // Close dialogs on transaction type change
  useEffect(() => {
    if (transactionTypeConfirmed && hasStepFlow(currentTab, loanType, creditCardType)) {
      return;
    }
    handleOnCloseDialog();
  }, [currentTab, loanType, creditCardType, transactionTypeConfirmed]);

  // Close category dialogs for convert_installment
  useEffect(() => {
    if (creditCardType === 'convert_installment') {
      setOpenDialogSelectedExpenseCategory(false);
      setOpenDialogSelectedIncomeCategory(false);
    }
  }, [creditCardType]);

  // Auto-focus amount input
  useEffect(() => {
    if (amount) return;
    const focusTimer = setTimeout(() => {
      if (amountInputRef.current) {
        amountInputRef.current.focus();
        amountInputRef.current.click();
      }
    }, 0);
    return () => clearTimeout(focusTimer);
  }, [currentTab, loanType]);

  // Initialize loan wallet from URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loanWalletId = params.get('loanWallet');
    const debtWalletId = params.get('debtWallet');
    if (loanWalletId) {
      const walletData = getWalletById(loanWalletId || '');
      if (!walletData || !walletData.name) return;
      setInitialLoanWallet({ id: loanWalletId, title: walletData.name, wallet: walletData });
    } else if (debtWalletId) {
      const walletData = getWalletById(debtWalletId || '');
      if (!walletData || !walletData.name) return;
      setLoanType('lend');
      setInitialDebtWallet({ id: debtWalletId, title: walletData.name, wallet: walletData });
    }
  }, [location.search]);

  // Force close keyboard when dialog opens
  useEffect(() => {
    const shouldForceDone = openTransactionTypeSelector || openDatePicker || openDialogSelectWallets ||
      openDialogSelectDestinationWallets || openDialogSelectedExpenseCategory || openDialogSelectedIncomeCategory;
    if (!shouldForceDone) return;
    const el = document.getElementById('keyboard-layout');
    if (!el) return;
    el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true }));
  }, [openTransactionTypeSelector, openDatePicker, openDialogSelectWallets, openDialogSelectDestinationWallets, openDialogSelectedExpenseCategory, openDialogSelectedIncomeCategory]);

  return {
    selectorCallback,
    handleAmountFocus,
    validateAmount,
  };
};
